import type { SxProps, Theme } from "@mui/material/styles";
import type { ReactNode } from "react";

import { Box, Paper } from "@mui/material";

import useIsWideScreen from "~/hooks/useIsWideScreen";

/** Height in px reserved for the fixed footer (padding + content). */
const FOOTER_HEIGHT = 72;

interface MobilePageLayoutProps {
	/** Scrollable page content. */
	children: ReactNode;
	/** Action footer (e.g. ActionBar for submit). On mobile, takes priority over mobileNav. */
	footer?: ReactNode;
	/** Sticky header slot — context info, search bar, or custom header. */
	header?: ReactNode;
	/** Maximum width of the content area. Default: 800. */
	maxWidth?: number;
	/** Bottom navigation shown only on mobile. Hidden when `footer` is provided. */
	mobileNav?: ReactNode;
	/** Additional styles for the content area. */
	sx?: SxProps<Theme>;
}

/**
 * Standard three-zone shell for mobile-first pages: sticky header, scrollable content, and a fixed
 * footer (an action bar or bottom navigation). Handles iOS safe-area insets and footer spacing so
 * feature pages only supply content. On wide screens the header/footer become inline-sticky.
 */
const MobilePageLayout = ({ children, footer, header, maxWidth = 800, mobileNav, sx }: MobilePageLayoutProps) => {
	const isWideScreen = useIsWideScreen();

	if (!isWideScreen) {
		const mobileFooter = footer ?? mobileNav;

		return (
			<Box sx={{ display: "flex", flexDirection: "column", minHeight: "100dvh" }}>
				{header && (
					<Box
						sx={{
							bgcolor: "background.paper",
							position: "sticky",
							pt: "env(safe-area-inset-top)",
							top: 0,
							zIndex: 10
						}}
					>
						{header}
					</Box>
				)}

				<Box
					sx={{
						flex: 1,
						maxWidth,
						mx: "auto",
						p: { sm: 3, xs: 2 },
						pb: mobileFooter ? `${FOOTER_HEIGHT + 16}px` : undefined,
						width: "100%",
						...sx
					}}
				>
					{children}
				</Box>

				{mobileFooter && (
					<Paper elevation={3} sx={{ bottom: 0, left: 0, position: "fixed", right: 0, zIndex: 1100 }}>
						{mobileFooter}
					</Paper>
				)}
			</Box>
		);
	}

	return (
		<Box sx={{ display: "flex", flexDirection: "column", maxWidth, mx: "auto", width: "100%" }}>
			{header && (
				<Box
					sx={{
						bgcolor: "background.paper",
						borderBottom: 1,
						borderColor: "divider",
						position: "sticky",
						top: 0,
						zIndex: 10
					}}
				>
					{header}
				</Box>
			)}

			<Box sx={{ flex: 1, px: { sm: 3, xs: 2 }, py: 2, ...sx }}>{children}</Box>

			{footer && (
				<Box
					sx={{
						bgcolor: "background.paper",
						borderColor: "divider",
						borderTop: 1,
						bottom: 0,
						position: "sticky",
						zIndex: 10
					}}
				>
					{footer}
				</Box>
			)}
		</Box>
	);
};

export default MobilePageLayout;
